import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { actionBankAPI } from "./actionBankAPI";

/**
 * Writing an action_catalog row changes what /action-banks/by-bank answers —
 * the server materializes one from the other inside the request — but the two
 * live in separate createApi slices, and RTK Query tags do not cross a slice
 * boundary. `invalidatesTags: ["tagGetActions"]` therefore leaves every
 * action-bank subscriber holding stale data.
 *
 * That subscriber is not hypothetical: the monitoring control panel renders
 * its buttons from useBankActions -> useGetActionBanksByBankQuery. Without
 * this, an operator who adds or deletes an action keeps seeing the old button
 * set on a panel that is already mounted.
 *
 * api.util.invalidateTags is RTK Query's own answer for cross-slice
 * invalidation; the alternative is merging the two slices, which would touch
 * every consumer of both for no behavioural gain beyond this.
 *
 * Deliberately fired after the write settles, not optimistically: an
 * invalidation on a request that then fails would refetch for nothing and, on
 * a slow link, briefly show the pre-write state as though it were the answer.
 */
export const invalidateActionBanksAfterWrite = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
    dispatch(actionBankAPI.util.invalidateTags(["tagGetActionBanks"]));
  } catch {
    // The write failed, so nothing downstream changed and nothing needs to
    // be refetched.
  }
};

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const actionAPI = createApi({
  reducerPath: "actionAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetActions"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ACTIONS */
    getActions: builder.query({
      query: (body: any) => ({
        url: `/action-catalogs/?page=${body.page}&limit=${body.limit}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActions"],
    }),
    //* *********************************************************** */
    //* GET ACTION CATALOGS BY ORGANIZATION */
    getActionCatalogsByOrganization: builder.query({
      query: (body: any) => ({
        url: `/action-catalogs/by-organization/${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActions"],
    }),
    //* *********************************************************** */
    //* CREATE ACTION */
    postAction: builder.mutation({
      query: (body: any) => ({
        url: "/action-catalogs/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetActions"],
      onQueryStarted: invalidateActionBanksAfterWrite,
    }),
    //* *********************************************************** */
    //* DELETE ACTION */
    deleteAction: builder.mutation({
      query: (body: any) => ({
        url: `/action-catalogs/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetActions"],
      onQueryStarted: invalidateActionBanksAfterWrite,
    }),
    //* *********************************************************** */
    //* EDIT ACTION */
    putAction: builder.mutation({
      query: (body: any) => ({
        url: `/action-catalogs/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetActions"],
      onQueryStarted: invalidateActionBanksAfterWrite,
    }),
  }),
});

export const {
  useGetActionsQuery,
  useLazyGetActionCatalogsByOrganizationQuery,
  usePostActionMutation,
  usePutActionMutation,
  useDeleteActionMutation,
} = actionAPI;
